import {
  LEGACY_REPORT_AUTH_TOKEN,
  REPORT_BRIDGE_FETCH,
  REPORT_BRIDGE_FETCH_RESULT,
  REPORT_BRIDGE_VERSION,
} from "./report-host-bridge.js";

/**
 * The script the dashboard puts at the top of a report before rendering it, so
 * reports written before the host bridge keep refreshing without a token. Port
 * of dashboard-v2 `app/utils/report-frame-shim.ts`.
 *
 * Those reports call `fetch("{apiHost}/api/v1/reports/{token}")` themselves,
 * with a Bearer token they used to get from the dashboard (or, before that,
 * from the `jwt_token` cookie, which throws in a sandbox). The shim:
 *
 * - takes over `fetch` for a GET to the report refresh route on the API's
 *   origin and asks the parent to run it over the bridge (`charidy-auth:fetch`);
 *   the parent runs it with its own session and only the answer comes back. The
 *   report's own headers, the placeholder token among them, are not passed on.
 *   Every other request, the same path on another origin included, goes to the
 *   browser's `fetch` unchanged, with no credential;
 * - answers `document.cookie`, which throws in the sandbox, with the
 *   placeholder token as `jwt_token`, so a report that reads the cookie gets as
 *   far as its refresh call instead of failing on the read.
 *
 * It holds nothing secret: it runs with the report's own privileges, and the
 * parent still decides what the bridge runs. A report that goes around it
 * reaches the API with no credential.
 *
 * Plain ES5 in a string, because it runs inside the report's document.
 *
 * @param {string} apiBaseUrl base URL of the public API (`publicApiHost`). Only
 *   its origin is used; when it does not parse, the shim takes over no request.
 */
export function reportFrameShimSource(apiBaseUrl) {
  return `(function () {
  'use strict';
  var parentWindow = window.parent;
  if (!parentWindow || parentWindow === window || typeof window.fetch !== 'function') return;

  var API_ORIGIN = ${scriptString(apiOrigin(apiBaseUrl))};
  var REFRESH_PATH = /^(?:\\/c)?\\/api\\/v1\\/reports\\/[\\w-]{1,256}$/;
  var NULL_BODY_STATUS = { 204: true, 205: true, 304: true };
  var nativeFetch = window.fetch;
  var pending = {};
  var sequence = 0;

  try {
    void document.cookie;
  } catch (sandboxed) {
    try {
      Object.defineProperty(document, 'cookie', {
        configurable: true,
        get: function () { return 'jwt_token=${LEGACY_REPORT_AUTH_TOKEN}'; },
        set: function () {}
      });
    } catch (ignored) {}
  }

  window.addEventListener('message', function (event) {
    var data = event.data;
    if (event.source !== parentWindow || !data || data.type !== '${REPORT_BRIDGE_FETCH_RESULT}' || data.v !== ${REPORT_BRIDGE_VERSION}) return;
    var waiting = pending[data.id];
    if (!waiting) return;
    delete pending[data.id];
    if (data.error) {
      waiting.reject(new TypeError('Failed to fetch'));
      return;
    }
    var status = Number(data.status);
    if (!(status % 1 === 0 && status >= 200 && status <= 599)) status = 502;
    var headers = data.contentType ? { 'Content-Type': String(data.contentType) } : {};
    var body = NULL_BODY_STATUS[status] ? null : String(data.body == null ? '' : data.body);
    try {
      waiting.resolve(new Response(body, { status: status, headers: headers }));
    } catch (unreadable) {
      waiting.reject(new TypeError('Failed to fetch'));
    }
  });

  function refreshUrl(input, init) {
    var url = input;
    var method = 'GET';
    if (typeof Request === 'function' && input instanceof Request) {
      url = input.url;
      method = input.method;
    }
    if (init && init.method) method = init.method;
    if (String(method).toUpperCase() !== 'GET') return null;
    var parsed;
    try {
      parsed = new URL(String(url));
    } catch (relative) {
      return null;
    }
    if (!API_ORIGIN || parsed.origin !== API_ORIGIN || parsed.username || parsed.password) return null;
    return REFRESH_PATH.test(parsed.pathname) ? parsed.href : null;
  }

  window.fetch = function (input, init) {
    var url = refreshUrl(input, init);
    if (!url) return nativeFetch.apply(window, arguments);
    return new Promise(function (resolve, reject) {
      var id = 'charidy-report-shim-' + (++sequence);
      pending[id] = { resolve: resolve, reject: reject };
      parentWindow.postMessage({ type: '${REPORT_BRIDGE_FETCH}', v: ${REPORT_BRIDGE_VERSION}, id: id, url: url, method: 'GET' }, '*');
    });
  };
})();`;
}

/** The origin of `apiBaseUrl`, or '' when it has none. */
function apiOrigin(apiBaseUrl) {
  try {
    const { origin } = new URL(apiBaseUrl);
    return origin === "null" ? "" : origin;
  } catch (e) {
    return "";
  }
}

/** A JS string literal that cannot close the `<script>` it sits in. */
function scriptString(value) {
  return JSON.stringify(value).replace(/</g, "\\u003c");
}

/** True when an opening `<name` tag starts at `index`, and not a longer name such as `<header`. */
function startsTag(html, index, name) {
  if (html[index] !== "<" || html.slice(index + 1, index + 1 + name.length).toLowerCase() !== name)
    return false;
  const next = html[index + 1 + name.length];
  return next === undefined || next === ">" || next === "/" || /\s/.test(next);
}

/**
 * The index just past the `>` that closes the tag starting at `index`, or -1.
 * A `>` inside a quoted attribute value does not close it.
 */
function tagEnd(html, index) {
  let i = index + 1;
  while (i < html.length) {
    const char = html[i];
    if (char === ">") return i + 1;
    i++;
    if (char !== "=") continue;
    while (i < html.length && /\s/.test(html[i])) i++;
    const quote = html[i];
    if (quote === '"' || quote === "'") {
      const close = html.indexOf(quote, i + 1);
      if (close === -1) return -1;
      i = close + 1;
    }
  }
  return -1;
}

/**
 * Where the shim goes: right after the opening `<head>` tag, else after
 * `<html>`, else after the doctype, else at the very start. Only the
 * document's prolog is read: leading whitespace and comments, the doctype, and
 * the opening `<html>` and `<head>` tags. A `<head>` further on, in the body or
 * inside a script's string, is the report's content, not its head; and the
 * shim never goes before the doctype, which would put the report in quirks
 * mode.
 */
function shimPosition(html) {
  let i = 0;
  let afterDoctype = -1;
  let afterHtml = -1;

  while (i < html.length) {
    if (/[\s\uFEFF]/.test(html[i])) {
      i++;
    } else if (html.startsWith("<!--", i)) {
      const close = html.indexOf("-->", i + 4);
      if (close === -1) break;
      i = close + 3;
    } else if (
      afterDoctype === -1 &&
      afterHtml === -1 &&
      /^<!doctype/i.test(html.slice(i, i + 9))
    ) {
      const close = html.indexOf(">", i);
      if (close === -1) break;
      i = afterDoctype = close + 1;
    } else if (afterHtml === -1 && startsTag(html, i, "html")) {
      const end = tagEnd(html, i);
      if (end === -1) break;
      i = afterHtml = end;
    } else if (startsTag(html, i, "head")) {
      const end = tagEnd(html, i);
      if (end !== -1) return end;
      break;
    } else {
      break;
    }
  }

  if (afterHtml !== -1) return afterHtml;
  if (afterDoctype !== -1) return afterDoctype;
  return 0;
}

/**
 * The report's HTML with the frame shim in front of its own scripts (see
 * {@link shimPosition} for where).
 */
export function withReportFrameShim(html, apiBaseUrl) {
  const at = shimPosition(html);
  return `${html.slice(0, at)}<script>${reportFrameShimSource(apiBaseUrl)}</script>${html.slice(
    at
  )}`;
}
